import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { agencyAppointmentAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const EMPTY = {
  carrier_id: '', contract_type_id: '', level_id: '',
  writing_number: '', appointment_status: 'Pending', effective_date: '', notes: '',
};

/**
 * AddAgencyAppointmentDialog — create/edit a single agency-level carrier
 * appointment. Owns its own form state and API calls; parent supplies the
 * editing target (or `null` for create).
 */
export const AddAgencyAppointmentDialog = ({
  open, onOpenChange, agencyId, editing,
  allCarriers = [], allContractTypes = [], allLevels = [],
  onSaved,
}) => {
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        carrier_id: editing.carrier_id,
        contract_type_id: editing.contract_type_id || '',
        level_id: editing.level_id || '',
        writing_number: editing.writing_number || '',
        appointment_status: editing.appointment_status,
        effective_date: editing.effective_date?.split('T')[0] || '',
        notes: editing.notes || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, editing]);

  const contractTypes = allContractTypes.filter((ct) => ct.carrier_id === form.carrier_id);
  const levels = allLevels
    .filter((l) => l.carrier_id === form.carrier_id)
    .sort((a, b) => b.level_rank - a.level_rank);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.carrier_id) { toast.error('Please select a carrier'); return; }
    if (!form.level_id) { toast.error('Please select a level'); return; }
    setSaving(true);
    try {
      const payload = {
        carrier_id: form.carrier_id,
        contract_type_id: form.contract_type_id || null,
        level_id: form.level_id,
        writing_number: form.writing_number || null,
        appointment_status: form.appointment_status,
        effective_date: form.effective_date || null,
        notes: form.notes || null,
      };
      if (editing) {
        await agencyAppointmentAPI.update(agencyId, editing.appointment_id, payload);
        toast.success('Appointment updated');
      } else {
        await agencyAppointmentAPI.create(agencyId, payload);
        toast.success('Appointment created');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save appointment'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="agency-appt-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editing ? 'Edit Agency Appointment' : 'Add Agency Appointment'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Carrier *</Label>
              <Select
                value={form.carrier_id}
                onValueChange={(v) => setForm((p) => ({ ...p, carrier_id: v, contract_type_id: '', level_id: '' }))}
                disabled={!!editing}
              >
                <SelectTrigger data-testid="agency-appt-carrier-select"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>
                  {allCarriers.map((c) => (
                    <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.carrier_id && contractTypes.length > 0 && (
              <div className="space-y-2">
                <Label>Contract Type (Optional)</Label>
                <Select
                  value={form.contract_type_id || '__none__'}
                  onValueChange={(v) => setForm((p) => ({ ...p, contract_type_id: v === '__none__' ? '' : v }))}
                >
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {contractTypes.map((ct) => (
                      <SelectItem key={ct.contract_type_id} value={ct.contract_type_id}>{ct.contract_type_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Level *</Label>
              <Select value={form.level_id} onValueChange={(v) => setForm((p) => ({ ...p, level_id: v }))} disabled={!form.carrier_id}>
                <SelectTrigger data-testid="agency-appt-level-select"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  {levels.map((l) => (
                    <SelectItem key={l.level_id} value={l.level_id}>{l.level_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={form.appointment_status} onValueChange={(v) => setForm((p) => ({ ...p, appointment_status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Terminated">Terminated</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Writing Number</Label>
              <Input
                value={form.writing_number}
                onChange={(e) => setForm((p) => ({ ...p, writing_number: e.target.value }))}
                placeholder="Enter writing number"
                data-testid="agency-appt-writing-number"
              />
            </div>

            <div className="space-y-2">
              <Label>Effective Date</Label>
              <Input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm((p) => ({ ...p, effective_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-agency-appt-btn">
              {saving ? 'Saving…' : editing ? 'Update Appointment' : 'Create Appointment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddAgencyAppointmentDialog;
